import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, FileText } from 'lucide-react';
import { dispatchFetch, T, F, bd } from '@/lib/dispatchApi';
import BricksLoader from "@/components/BricksLoader";

function fmtBytes(bytes) {
  if (!bytes) return '—';
  const gb = bytes / (1024 ** 3);
  if (gb >= 1) return `${gb.toFixed(1)} GB`;
  return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
}

const PILL_COLORS = {
  ok:      { bg: '#4A6B3C33', fg: T.okText },
  brick:   { bg: '#D2542B33', fg: T.moon },
  neutral: { bg: T.nightDeep, fg: T.moonSoft },
};

function jobPill(job) {
  if (job.status === 'completed') return { label: 'Delivered', tone: 'ok' };
  if (job.status === 'in_progress') return { label: 'In progress', tone: 'neutral' };
  return { label: 'Scheduled', tone: 'neutral' };
}

export default function DispatchUploadsPage() {
  const [uploads, setUploads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [upRes, jobRes] = await Promise.all([
        dispatchFetch('/api/uploads'),
        dispatchFetch('/api/jobs'),
      ]);
      if (!upRes.ok) throw new Error(`Uploads ${upRes.status}`);
      if (!jobRes.ok) throw new Error(`Jobs ${jobRes.status}`);
      setUploads(await upRes.json());
      setJobs(await jobRes.json());
    } catch (e) {
      console.error('Uploads fetch', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const activeJobs = jobs.filter(j => j.status === 'in_progress');
    if (activeJobs.length === 0) {
      alert('No active jobs — start a job before uploading.');
      return;
    }
    const targetJob = activeJobs[0];
    setUploading(true);
    try {
      const form = new FormData();
      form.append('job_id', targetJob.id);
      form.append('filename', file.name);
      form.append('file_size', String(file.size));
      form.append('mime_type', file.type || 'application/octet-stream');
      form.append('file', file);
      const res = await dispatchFetch('/api/uploads', { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      await fetchData();
    } catch {
      alert('Upload failed — check your connection.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const jobMap = new Map(jobs.map(j => [j.id, j]));
  const groupMap = new Map();
  for (const u of uploads) {
    const job = jobMap.get(u.job_id);
    if (!job) continue;
    if (!groupMap.has(u.job_id)) groupMap.set(u.job_id, { job, uploads: [] });
    groupMap.get(u.job_id).uploads.push(u);
  }
  const groups = Array.from(groupMap.values());
  const inProgress = groups.filter(g => g.job.status === 'in_progress');
  const recent = groups.filter(g => g.job.status !== 'in_progress');
  const px = 24;

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100%', paddingLeft: px, paddingRight: px, paddingTop: 16, paddingBottom: 32, color: T.moon, fontFamily: F.heading }}>
      <p style={{ fontSize: 26, color: T.moon, fontFamily: F.heading, letterSpacing: -0.4, marginBottom: 4 }}>Upload</p>
      <p style={{ fontSize: 12, color: T.moonFaint, marginBottom: 20 }}>Drop the card — we auto-sort by job.</p>

      {/* Hidden file input */}
      <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFileChange} />

      {/* Drop zone */}
      <div
        className="bx-tap"
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file && fileRef.current) {
            const dt = new DataTransfer();
            dt.items.add(file);
            fileRef.current.files = dt.files;
            await handleFileChange({ target: { files: dt.files } });
          }
        }}
        style={{
          ...bd(T.nightHairS, 1.5), borderRadius: 2, borderStyle: 'dashed',
          paddingTop: 36, paddingBottom: 36, paddingLeft: 20, paddingRight: 20,
          display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28,
          cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1,
        }}
      >
        {uploading ? (
          <BricksLoader variant="assemble" size={40} color={T.brick} />
        ) : (
          <>
            <Upload size={28} color={T.moonMute} strokeWidth={1.5} />
            <p style={{ fontSize: 15, color: T.moon, fontFamily: F.heading, marginTop: 12, marginBottom: 6 }}>Click or drag to upload</p>
            <p style={{ fontSize: 11, color: T.moonFaint, marginBottom: 14 }}>RAW, JPEG, MP4 · up to 20 GB per job</p>
            {/* The one main action. The click bubbles to the drop zone, which opens the picker. */}
            <button type="button" data-testid="upload-choose" style={{ display: 'inline-flex', alignItems: 'center', height: 48, padding: '0 16px', boxSizing: 'border-box', backgroundColor: T.brick, color: '#14110E', border: 'none', borderRadius: 2, fontFamily: F.heading, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              Choose files
            </button>
          </>
        )}
      </div>

      {/* In progress */}
      {inProgress.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 10 }}>In progress</p>
          {inProgress.map(({ job, uploads: ups }) => {
            const totalBytes = ups.reduce((s, u) => s + u.file_size, 0);
            const pct = job.deliverables_count > 0 ? Math.round((job.uploaded_count / job.deliverables_count) * 100) : null;
            return (
              <div key={job.id} style={{ backgroundColor: T.nightSoft, ...bd(T.nightHairS), borderRadius: 2, padding: 16, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: pct != null ? 12 : 0 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, color: T.moon, marginBottom: 4 }}>{job.address}</p>
                    <p style={{ fontFamily: F.mono, fontSize: 11, color: T.moonFaint, margin: 0 }}>{ups.length} file{ups.length !== 1 ? 's' : ''} · {fmtBytes(totalBytes)}</p>
                  </div>
                  {pct != null && <span style={{ fontFamily: F.mono, fontSize: 16, color: T.moon }}>{pct}%</span>}
                </div>
                {pct != null && (
                  <div style={{ height: 3, backgroundColor: T.nightHair, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: T.moon, borderRadius: 2 }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Recent uploads */}
      {recent.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 10 }}>Recent uploads</p>
          <div style={{ backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
            {recent.map(({ job, uploads: ups }, i) => {
              const totalBytes = ups.reduce((s, u) => s + u.file_size, 0);
              const pill = jobPill(job);
              const colors = PILL_COLORS[pill.tone];
              return (
                <div key={job.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, ...(i < recent.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}>
                  <FileText size={18} color={T.moonFaint} strokeWidth={1.5} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 13, color: T.moon, marginBottom: 3 }}>{job.address}</p>
                    <p style={{ fontFamily: F.mono, fontSize: 11, color: T.moonFaint, margin: 0 }}>{ups.length} file{ups.length !== 1 ? 's' : ''} · {fmtBytes(totalBytes)}</p>
                  </div>
                  <span style={{ fontSize: 12, color: colors.fg, whiteSpace: 'nowrap' }}>{pill.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && groups.length === 0 && (
        <div style={{ paddingTop: 40, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: T.moonFaint }}>No uploads yet</p>
        </div>
      )}
    </div>
  );
}
